// Browser-safe helpers for creator profile pages (/library/by/<slug>).

import { hubSlug } from "./library-hubs";

export type ParsedAuthor = {
  /** Display name without the handle, e.g. "Julien Thibeaut". */
  name: string;
  /** Handle without the leading @, e.g. "ibelick" — null when absent. */
  handle: string | null;
};

/** Splits "Julien Thibeaut (@ibelick)" into its display name and handle. */
export function parseAuthor(raw: string): ParsedAuthor {
  const value = (raw ?? "").trim();
  const m = value.match(/^(.*?)\s*\(\s*@([A-Za-z0-9_.-]+)\s*\)\s*$/);
  if (m) {
    const name = (m[1] ?? "").trim();
    return { name: name || `@${m[2]}`, handle: m[2] ?? null };
  }
  const bare = value.match(/^@([A-Za-z0-9_.-]+)$/);
  if (bare) return { name: value, handle: bare[1] ?? null };
  return { name: value, handle: null };
}

/** Stable URL slug for a creator, derived from the display part of the author. */
export function creatorSlug(raw: string): string {
  const { name, handle } = parseAuthor(raw);
  return hubSlug(name) || hubSlug(handle ?? "") || "unknown";
}

/** Creators need at least this many gems to be indexed and listed. */
export const CREATOR_MIN_ITEMS = 2;

function joinList(values: string[]): string {
  if (values.length === 0) return "";
  if (values.length === 1) return values[0]!;
  return `${values.slice(0, -1).join(", ")} and ${values[values.length - 1]}`;
}

export function creatorIntro(
  name: string,
  count: number,
  categories: string[],
): string {
  const noun = count === 1 ? "gem" : "gems";
  const where = categories.length
    ? `, across ${joinList(categories.slice(0, 3))}`
    : "";
  return `${count} ${noun} by ${name} featured in the Vibing Library${where}. Every entry was found, checked and written up by hand — follow the links to go straight to the source.`;
}
